
import { defineComponent, onMounted, ref, watchEffect, computed } from 'vue';
import { Apollo, Notify } from '@/core/services';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { GET_ORDER_DETAIL, RESEND_EMAIL, GET_ORDER_STATUS } from '../graphql/Queries';
import { UPDATE_ORDER_STATUS } from '../graphql/Mutations';
import { useRoute } from 'vue-router';
import { setPageHistory } from '@/core/helpers/toolbar';
import Table from '../../../components/Table/Table.vue';
import { generateAvatar } from '@/core/helpers/generateAvatar';
import InnerLoader from '../../../components/InnerLoader.vue';
import { Select } from '@/components/input-elements';
import { asideTheme } from '@/core/helpers/config';
import Axios from '@/core/services/Http';
export default defineComponent({
    name: 'order',
    components: { Table, InnerLoader, Select },

    setup() {
        const route = useRoute();
        const i18n = useI18n();
        const store = useStore();
        const loader = ref(false);
        const orders = ref([]) as Record<any, any>;
        const customer = ref({}) as Record<any, any>;
        const orderTotals = ref({}) as Record<any, any>;
        const system_locale = ref();
        const sendMailLoader = ref(false);
        const sapResendLoader = ref(false);
        const order_status: any = ref([]);
        const statusList = ref([]) as Record<any, any>;
        const orderStatusList = ref([]) as Record<any, any>;
        const defaultImage = ref(require('@/assets/media/images/bridge_placeholder.png'));

        const getProductImage = (item: Record<any, any>) => {
            const filePath = item?.product?.media?.[0]?.file_path;
            return filePath || defaultImage.value;
        };

        const onProductImageError = (event: Event) => {
            const target = event.target as HTMLImageElement;
            if (target.src !== defaultImage.value) {
                target.src = defaultImage.value;
            }
        };

        const deliveryPlanning = computed(() => {
            const raw = orders.value?.delivery_planning;
            if (!raw) {
                return null;
            }
            try {
                return typeof raw === 'string' ? JSON.parse(raw) : raw;
            } catch {
                return null;
            }
        });

        const skipReasonLabel = (reason: string) => {
            if (reason === 'weekend') {
                return 'Weekend';
            }
            if (reason === 'uk_bank_holiday') {
                return 'UK bank holiday';
            }
            return reason || '—';
        };

        const formatDeliveryDate = (value: string | null | undefined) => {
            if (!value) {
                return '—';
            }
            // Backend may send "Y-m-d" or "Y-m-d H:i:s" — use date part only
            const datePart = String(value).trim().split(/[\sT]/)[0];
            const date = new Date(`${datePart}T00:00:00`);
            if (Number.isNaN(date.getTime())) {
                return datePart;
            }
            return date.toLocaleDateString('en-GB', {
                day: 'numeric',
                month: 'short',
                year: 'numeric'
            });
        };

        const formatCutoffMet = (value: boolean | null | undefined) => {
            if (value === true) {
                return 'Yes';
            }
            if (value === false) {
                return 'No';
            }
            return '—';
        };

        const formatPlacedAt = (planning: Record<any, any> | null) => {
            if (!planning?.placed_at) {
                return '—';
            }
            const date = new Date(planning.placed_at);
            if (Number.isNaN(date.getTime())) {
                return planning.placed_at;
            }
            const formatted = date.toLocaleString('en-GB', {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
            });
            return planning.placed_day ? `${formatted} (${planning.placed_day})` : formatted;
        };

        const placedAtDisplay = computed(() => formatPlacedAt(deliveryPlanning.value));

        const skippedDatesList = computed(() => {
            const planning = deliveryPlanning.value;
            if (!planning?.skipped_dates?.length) {
                return [];
            }
            const reasons = planning.skip_reasons || {};
            return planning.skipped_dates.map((date: string) => ({
                date,
                reason: skipReasonLabel(reasons[date])
            }));
        });

        const decodeHtmlEntities = (value: string) => {
            if (!/&(?:lt|gt|amp|quot|#39|#x3c|#x3e);/i.test(value)) {
                return value;
            }
            const textarea = document.createElement('textarea');
            textarea.innerHTML = value;
            return textarea.value;
        };

        const getXmlElementsByLocalName = (root: Document | Element, localName: string) => {
            const target = localName.toLowerCase();
            return Array.from(root.getElementsByTagName('*')).filter(el => {
                const tag = (el.localName || el.tagName || '').toLowerCase();
                return tag === target || tag.endsWith(`:${target}`);
            });
        };

        const firstXmlText = (root: Document | Element, localName: string) => {
            const el = getXmlElementsByLocalName(root, localName)[0];
            return el?.textContent?.replace(/\s+/g, ' ').trim() || '';
        };

        const collectPlainLines = (text: string) =>
            text
                .split(/\r?\n/)
                .map(line => line.trim())
                .filter(line => line && !line.startsWith('<') && line !== '--- SAP Errors ---');

        const parseSapJsonMessages = (value: unknown, bag: string[]) => {
            if (value == null) {
                return;
            }
            if (typeof value === 'string') {
                const trimmed = value.trim();
                if (trimmed && !trimmed.startsWith('<')) {
                    bag.push(trimmed);
                }
                return;
            }
            if (Array.isArray(value)) {
                value.forEach(item => parseSapJsonMessages(item, bag));
                return;
            }
            if (typeof value !== 'object') {
                return;
            }
            const record = value as Record<string, unknown>;
            const preferred = ['message', 'error', 'errorMessage', 'error_message', 'faultstring', 'note', 'text', 'description', 'Message', 'Error', 'Note'];
            preferred.forEach(key => {
                if (typeof record[key] === 'string' && record[key]) {
                    bag.push(String(record[key]).trim());
                } else if (record[key] && typeof record[key] === 'object') {
                    parseSapJsonMessages(record[key], bag);
                }
            });
            Object.keys(record).forEach(key => {
                if (!preferred.includes(key)) {
                    parseSapJsonMessages(record[key], bag);
                }
            });
        };

        const parseSapXmlMessages = (xml: string) => {
            const messages: string[] = [];
            const parser = new DOMParser();
            const doc = parser.parseFromString(xml, 'text/xml');
            if (doc.getElementsByTagName('parsererror').length) {
                return messages;
            }

            const items = getXmlElementsByLocalName(doc, 'Item');
            const errorNotes: string[] = [];
            const otherNotes: string[] = [];

            items.forEach(item => {
                const note = firstXmlText(item, 'Note') || firstXmlText(item, 'Message') || firstXmlText(item, 'Text');
                const severity = firstXmlText(item, 'SeverityCode');
                if (!note) {
                    return;
                }
                if (severity === '3' || severity === '4' || /error|abort/i.test(severity)) {
                    errorNotes.push(note);
                } else {
                    otherNotes.push(note);
                }
            });

            if (errorNotes.length) {
                messages.push(...errorNotes);
            } else if (otherNotes.length) {
                messages.push(...otherNotes);
            }

            ['faultstring', 'Reason', 'ErrorMessage', 'errorMessage', 'MessageText', 'StandardMessage'].forEach(tag => {
                const text = firstXmlText(doc, tag);
                if (text) {
                    messages.push(text);
                }
            });

            getXmlElementsByLocalName(doc, 'Text').forEach(el => {
                const parent = (el.parentElement?.localName || el.parentElement?.tagName || '').toLowerCase();
                if (parent.includes('reason') || parent.includes('fault')) {
                    const text = el.textContent?.replace(/\s+/g, ' ').trim();
                    if (text) {
                        messages.push(text);
                    }
                }
            });

            return messages;
        };

        const stripXmlToText = (value: string) =>
            value
                .replace(/<[^>]+>/g, ' ')
                .replace(/\s+/g, ' ')
                .trim();

        const parseSapResponse = (response: string) => {
            const messages: string[] = [];
            const raw = decodeHtmlEntities(String(response ?? '')).trim();
            if (!raw) {
                return messages;
            }

            const marker = '--- SAP Errors ---';
            const markerIndex = raw.indexOf(marker);
            if (markerIndex !== -1) {
                const afterMarker = collectPlainLines(raw.slice(markerIndex + marker.length));
                if (afterMarker.length) {
                    messages.push(...afterMarker);
                }
            }

            if ((raw.startsWith('{') || raw.startsWith('[')) && !raw.includes('<')) {
                try {
                    parseSapJsonMessages(JSON.parse(raw), messages);
                    if (messages.length) {
                        return Array.from(new Set(messages.filter(Boolean)));
                    }
                } catch {
                    // continue with XML / plain text parsing
                }
            }

            const xmlChunks = raw.match(/<(?:\w+:)?(?:Envelope|Fault|n0:SalesOrder|Log)[\s\S]*?<\/(?:\w+:)?(?:Envelope|Fault|n0:SalesOrder|Log)>/gi) || [];
            const xmlToParse = xmlChunks.length ? xmlChunks : raw.includes('<') ? [raw] : [];

            xmlToParse.forEach(chunk => {
                try {
                    messages.push(...parseSapXmlMessages(chunk));
                } catch {
                    const stripped = stripXmlToText(chunk);
                    if (stripped) {
                        messages.push(stripped);
                    }
                }
            });

            let remainder = raw;
            xmlChunks.forEach(chunk => {
                remainder = remainder.replace(chunk, ' ');
            });
            collectPlainLines(remainder).forEach(line => messages.push(line));

            const unique = Array.from(new Set(messages.map(msg => msg.replace(/\s+/g, ' ').trim()).filter(Boolean)));
            if (unique.length) {
                return unique;
            }

            const stripped = stripXmlToText(raw);
            return stripped ? [stripped] : [raw];
        };

        const sapErrorMessages = computed(() => parseSapResponse(orders.value?.remote_response || ''));

        const isOrderSendFailed = computed(() => {
            const hasNoEconomicId = orders.value?.economic_order_id == null || orders.value?.economic_order_id === '';
            const hasNoEconomicNumber = orders.value?.economic_order_number == null || orders.value?.economic_order_number === '';
            return hasNoEconomicId && hasNoEconomicNumber;
        });

        const showSapErrorBox = computed(() => isOrderSendFailed.value && sapErrorMessages.value.length > 0);

        const erp_stats = ref(store.state.AuthModule.erp_stats);

        const showExternalOrderInfo = computed(() => {
            const hasOrderNumber = !!orders.value?.economic_order_number || !!orders.value?.economic_order_id;
            if (hasOrderNumber) {
                return true;
            }
            if (erp_stats.value?.erp_enable) {
                return isOrderSendFailed.value;
            }
            return showSapErrorBox.value;
        });
        const dhlTrackingUrl = process.env.VUE_APP_DHL_TRACKING_URL || 'https://track.dhlparcel.co.uk/?con=';

        const getDhlTrackingUrl = (trackingId: string) => `${dhlTrackingUrl}${trackingId}`;

        const columns = ref([
            {
                label: '',
                key: 'image'
            },
            {
                label: 'message.PRODUCT',
                key: 'description'
            },
            {
                label: 'message.QUANTITY',
                key: 'quantity'
            },
            {
                label: 'message.PRICE',
                key: 'price',
                textAlignment: 'end'
            },
            {
                label: 'message.TOTAL',
                key: 'total',
                textAlignment: 'end'
            }
        ]);

        watchEffect(() => {
            system_locale.value = i18n.fallbackLocale.value;
            statusList.value = [];
            orderStatusList.value.forEach(element => {
                if (element.id == 1 || element.id == 3 || element.id == 5 || element.id == 6 || element.id == 10) {
                    statusList.value.push({
                        label: element?.status ? JSON.parse(element.status)[system_locale.value]?.name : '',
                        value: element.id
                    });
                }
            });
        });

        const me = computed(() => {
            return store.getters.getUser;
        });

        const companyLogo = name => {
            return generateAvatar(name);
        };

        const updateStatus = async () => {
            loader.value = true;
            await Apollo.mutate({
                mutation: UPDATE_ORDER_STATUS,
                variables: { id: orders.value.id, order_status_id: order_status.value },
                update: () => {
                    loader.value = false;
                    Notify.success(`${i18n.t('message.RECORD_UPDATED_SUCCESSFULLY')}`);
                }
            }).catch(() => {
                loader.value = false;
            });
        };

        const orderDetails = uuid => {
            loader.value = true;
            Apollo.watchQuery({
                query: GET_ORDER_DETAIL,
                errorPolicy: 'all',
                fetchPolicy: 'network-only',
                nextFetchPolicy: 'cache-only',
                variables: {
                    uuid: uuid
                }
            }).subscribe(({ data, errors }) => {
                if (errors) {
                    loader.value = false;
                }
                orders.value = data.order_detail;
                customer.value = data?.order_detail?.customer;
                orderTotals.value = {};
                if (orders.value.order_total.length > 0) {
                    orders.value.order_total.forEach(element => {
                        orderTotals.value[element.code] = {
                            title: element.title,
                            value: element.value
                        };
                    });
                }
                loader.value = false;
            });
        };

        const orderStatus = () => {
            // loader.value = true;
            Apollo.watchQuery({
                query: GET_ORDER_STATUS,
                errorPolicy: 'all',
                fetchPolicy: 'network-only',
                nextFetchPolicy: 'cache-only'
            }).subscribe(({ data, errors }) => {
                if (errors) {
                    loader.value = false;
                }
                if (data?.orders_status.length > 0) {
                    orderStatusList.value = JSON.parse(data?.orders_status);
                }

                // loader.value = false;
            });
        };

        const resendMsg = order_id => {
            try {
                sendMailLoader.value = true;
                Apollo.watchQuery({
                    query: RESEND_EMAIL,
                    errorPolicy: 'all',
                    fetchPolicy: 'network-only',
                    nextFetchPolicy: 'cache-only',
                    variables: {
                        order_id: order_id
                    }
                }).subscribe(({ errors }) => {
                    if (errors) {
                        sendMailLoader.value = false;
                    }
                    sendMailLoader.value = false;
                    Notify.success(`${i18n.t('message.MESSAGE_SENT')}`);
                });
            } catch (e) {
                sendMailLoader.value = false;
                Notify.error(i18n.t('message.SOMETHING_WENT_WRONG'));
            }
        };

        const resendOrderEconomic = async order_id => {
            sapResendLoader.value = true;
            try {
                const response = await Axios.post('/sap/orders/' + order_id);
                const payload = response?.data?.data ?? response?.data ?? {};
                const orderNumber = payload?.economic_order_number || payload?.order_number;
                const economicOrderId = payload?.economic_order_id;

                if (orderNumber || economicOrderId) {
                    orders.value = {
                        ...orders.value,
                        economic_order_number: orderNumber || orders.value?.economic_order_number,
                        economic_order_id: economicOrderId ?? orders.value?.economic_order_id ?? orderNumber
                    };
                }

                Notify.success(i18n.t('message.SAP_ORDER_RESEND_SUCCESSFULLY'));
                await orderDetails(route.params.uuid);
            } catch ($e) {
                Notify.error($e?.response?.data?.message || $e?.message || i18n.t('message.SOMETHING_WENT_WRONG'));
            } finally {
                sapResendLoader.value = false;
            }
        };

        const clearPageHistory = () => {
            setPageHistory({});
        };
        onMounted(() => {
            orderDetails(route.params.uuid);
            orderStatus();
        });

        return {
            orders,
            erp_stats,
            me,
            customer,
            orderTotals,
            system_locale,
            columns,
            loader,
            clearPageHistory,
            companyLogo,
            i18n,
            asideTheme,
            resendMsg,
            resendOrderEconomic,
            sendMailLoader,
            sapResendLoader,
            updateStatus,
            statusList,
            order_status,
            defaultImage,
            getProductImage,
            onProductImageError,
            deliveryPlanning,
            skippedDatesList,
            formatDeliveryDate,
            formatCutoffMet,
            formatPlacedAt,
            placedAtDisplay,
            getDhlTrackingUrl,
            sapErrorMessages,
            showSapErrorBox,
            isOrderSendFailed,
            showExternalOrderInfo
        };
    }
});
